/**
 * FilterBar — search and filter controls for listings page
 *
 * Client component with HandiPrêt glassmorphism styling.
 * Syncs with URL search params.
 */

"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useTransition } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { CreateAlertDialog } from "@/components/create-alert-dialog";
import { LISTING_TYPES, ITEM_CONDITIONS, DEFAULT_CATEGORIES } from "@/lib/constants";

export function FilterBar() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const [isPending, startTransition] = useTransition();

    const currentType = searchParams.get("type") ?? "";
    const currentCondition = searchParams.get("condition") ?? "";
    const currentCategory = searchParams.get("categorie") ?? "";
    const currentQuery = searchParams.get("q") ?? "";
    const currentSort = searchParams.get("sort") ?? "createdAt";

    /** Update URL params without full page reload */
    const updateParams = useCallback(
        (key: string, value: string) => {
            const params = new URLSearchParams(searchParams.toString());

            if (value) {
                params.set(key, value);
            } else {
                params.delete(key);
            }

            // Reset page when changing filters
            if (key !== "page") params.delete("page");

            startTransition(() => {
                router.push(`/annonces?${params.toString()}`);
            });
        },
        [searchParams, router],
    );

    /** Clear all filters */
    const clearFilters = useCallback(() => {
        startTransition(() => {
            router.push("/annonces");
        });
    }, [router]);

    const hasActiveFilters =
        currentType || currentCondition || currentCategory || currentQuery;

    return (
        <div className="glass rounded-2xl p-6 space-y-4">
            {/* Search bar */}
            <div className="relative">
                <Search
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-hp-purple"
                    aria-hidden="true"
                />
                <Input
                    type="search"
                    placeholder="Rechercher du matériel..."
                    defaultValue={currentQuery}
                    className="pl-10 rounded-xl border-hp-gray/20 focus-visible:ring-hp-purple/40"
                    aria-label="Rechercher une annonce"
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            updateParams("q", e.currentTarget.value);
                        }
                    }}
                />
            </div>

            {/* Filter row */}
            <div className="flex flex-wrap items-center gap-3">
                <SlidersHorizontal
                    className="h-4 w-4 text-hp-purple"
                    aria-hidden="true"
                />

                {/* Type filter */}
                <Select
                    value={currentType}
                    onValueChange={(val) => updateParams("type", val)}
                >
                    <SelectTrigger className="w-36 rounded-xl border-hp-gray/20" aria-label="Filtrer par type">
                        <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">Tous les types</SelectItem>
                        {Object.entries(LISTING_TYPES).map(([key, type]) => (
                            <SelectItem key={key} value={key}>
                                {type.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {/* Condition filter */}
                <Select
                    value={currentCondition}
                    onValueChange={(val) => updateParams("condition", val)}
                >
                    <SelectTrigger className="w-40 rounded-xl border-hp-gray/20" aria-label="Filtrer par état">
                        <SelectValue placeholder="État" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">Tous les états</SelectItem>
                        {Object.entries(ITEM_CONDITIONS).map(([key, cond]) => (
                            <SelectItem key={key} value={key}>
                                {cond.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {/* Category filter */}
                <Select
                    value={currentCategory}
                    onValueChange={(val) => updateParams("categorie", val)}
                >
                    <SelectTrigger className="w-48 rounded-xl border-hp-gray/20" aria-label="Filtrer par catégorie">
                        <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">Toutes les catégories</SelectItem>
                        {DEFAULT_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.slug} value={cat.slug}>
                                {cat.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {/* Sort */}
                <Select
                    value={currentSort}
                    onValueChange={(val) => updateParams("sort", val)}
                >
                    <SelectTrigger className="w-40 rounded-xl border-hp-gray/20" aria-label="Trier par">
                        <SelectValue placeholder="Trier par" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="createdAt">Plus récentes</SelectItem>
                        <SelectItem value="price">Prix</SelectItem>
                        <SelectItem value="title">Titre</SelectItem>
                    </SelectContent>
                </Select>

                <CreateAlertDialog />

                {/* Clear filters */}
                {hasActiveFilters && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        disabled={isPending}
                        className="text-hp-purple hover:text-hp-purple-dark hover:bg-hp-purple/10 rounded-xl"
                    >
                        <X className="mr-1 h-3 w-3" aria-hidden="true" />
                        Effacer
                    </Button>
                )}
            </div>

            {/* Active filters badges */}
            {hasActiveFilters && (
                <div className="flex flex-wrap gap-2">
                    {currentType && currentType !== "ALL" && (
                        <Badge className="bg-hp-purple/10 text-hp-purple border-0 rounded-full">
                            {LISTING_TYPES[currentType as keyof typeof LISTING_TYPES]?.label}
                            <button
                                onClick={() => updateParams("type", "")}
                                className="ml-1 hover:text-hp-purple-dark"
                                aria-label="Retirer le filtre type"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    )}
                    {currentQuery && (
                        <Badge className="bg-hp-orange/10 text-hp-orange border-0 rounded-full">
                            &ldquo;{currentQuery}&rdquo;
                            <button
                                onClick={() => updateParams("q", "")}
                                className="ml-1 hover:text-hp-orange-dark"
                                aria-label="Retirer la recherche"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    )}
                </div>
            )}

            {isPending && (
                <p className="text-sm text-hp-purple font-medium animate-pulse" aria-live="polite">
                    Chargement...
                </p>
            )}
        </div>
    );
}
